<script setup>
import { Tooltip } from 'floating-vue';
import { breakpointsBootstrapV5, useBreakpoints } from '@vueuse/core';
import { onMounted, unref, useTemplateRef } from 'vue';
import cloneDeep from 'lodash.clonedeep';
import { ButtonTypes } from '@@/components/Common/Button.vue';
import { getMaskImageStyle } from '@@/utils/CommonUtils';
import { localStorageService } from '@@/utils/StorageService';
import BottomSheet from '@@/components/Common/BottomSheet.vue';
import DropdownMenu from '@@/components/Common/DropdownMenu.vue';

const filter = defineModel('filter', { type: Object, default: null });
const sort = defineModel('sort', { type: String, default: null });

const props = defineProps({
  containerClass: {
    type: String,
    default: null,
  },
  favoriteLocationTypes: {
    type: Array,
    default: null,
  },
  isFavorites: {
    type: Boolean,
    default: false,
  },
  isMapPage: {
    type: Boolean,
    default: false,
  },
  sortOptions: {
    type: Array,
    default: null,
  },
});

const $emit = defineEmits(['update:modelValue', 'dropdownClose', 'dropdownOpen']);

const newValue = ref(null);
const hasValueChanged = ref(false);

const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isLargeScreen = breakpoints.greaterOrEqual('lg');

const button = useTemplateRef('button');
const showFilters = ref(false);

const filterIconClass = 'tw-block tw-h-5 tw-w-5 tw-mx-auto';
const filterIconStyle = {
  backgroundColor: 'currentColor',
  ...getMaskImageStyle('https://blizzard.opensnow.com/icons/custom/sliders.svg'),
};

const sectionClass = 'tw-mt-2 first:tw-mt-0 lg:first:tw-mt-2 tw-mb-4 last:tw-mb-8';

// On large screens, a <DropdownMenu> component is rendered. And on small screens, a
// <BottomSheetComponent> is rendered.

const component = computed(() => isLargeScreen.value ? DropdownMenu : BottomSheet);
const componentProps = computed(() => {
  if (isLargeScreen.value) {
    return {
      cardProps: { hasBodyPaddingSmall: true, hasBodyShadow: true },
      position: 'tw-left-2',
      showBackgroundBlur: true,
      showMenu: showFilters.value,
      zIndex: 'tw-z-30',
    };
  }

  return {
    cardClassNames: 'tw-overscroll-contain',
    style: '--visible-height: 80vh;',
    visible: showFilters.value,
  };
});
const slotName = computed(() => isLargeScreen.value ? 'menu' : 'content');
const slotContentStyle = computed(() => isLargeScreen.value ? 'min-width: 30rem; width: 25vw;' : null);

const canShowSort = computed(() => props.sortOptions?.length > 0);
const canShowLocations = computed(() => Array.isArray(filter.value?.locationTypes) && locationTypes.value.length > 1);
const canShowCountries = computed(() => Array.isArray(filter.value?.countries));
const canShowCompareOptions = computed(() => canShowSort.value || canShowLocations.value || canShowCountries.value);

const { isAllAccess, isGuest } = useUser();
const isSortOptionLocked = (sortOption) => sortOption.isAllAccess && !isAllAccess.value;

const isSortOptionChecked = (sortOption) => {
  if (hasValueChanged.value && newValue.value?.sort) {
    return sortOption.field === newValue.value.sort;
  }

  return sortOption.field === sort.value;
};

const lockedIconProps = {
  class: 'tw-inline-block tw-w-4 tw-h-4 tw-align-middle',
  style: {
    backgroundColor: 'var(--saturated-teal)',
    ...getMaskImageStyle('https://blizzard.opensnow.com/icons/custom/green-lock.svg'),
  },
};

const isActive = computed(() => {
  if (showFilters.value) {
    return true;
  }

  if (canShowSort.value && sort.value) {
    const fields = props.sortOptions.map(({ field }) => field);

    // The default sort on the Favorite pages is "user" and the default sort on the Compare pages
    // is "alpha". So if a sort option has been selected other than "user" on a Favorites page or
    // a sort option other than "alpha" has been selected on a Compare page, then assume the user
    // has selected a sort option. This doesn't address the case where a user selected "user" or
    // "alpha" after previously selecting something different, but that's probably OK.

    if (fields.includes('user') && sort.value !== 'user') {
      return true;
    }
    else if (!fields.includes('user') && sort.value !== 'alpha') {
      return true;
    }
  };

  if (canShowLocations.value && filter.value.locationTypes.length > 0) {
    return true;
  }

  if (canShowCountries.value && filter.value.countries.length > 0) {
    return true;
  }

  return false;
});

const metaStore = useMetaStore();

const countries = [
  { label: 'Global', value: 'global' },
  ...metaStore.countries.map(({ code, name }) => ({
    isAllAccess: true,
    label: name,
    value: code,
  })),
];

const locationTypes = computed(() => {
  const all = { label: 'All', value: 'all' };
  const getLocationTypes = (locationTypes) => locationTypes
    .map(({ id, name }) => ({ isAllAccess: true, label: name, value: id }));

  if (props.isFavorites) {
    if (props.favoriteLocationTypes?.length > 1) {
      return [
        all,
        ...getLocationTypes(props.favoriteLocationTypes),
      ];
    }

    return [];
  }

  return [
    all,
    ...getLocationTypes(metaStore.skiAreaLocationTypes),
  ];
});

const selectedCountries = computed({
  get() {
    if (newValue.value?.filter.countries?.length > 0) {
      return newValue.value.filter.countries.map((country) => country.code);
    }

    return ['global'];
  },
  set(values) {
    const newCountries = metaStore.countries
      .filter((country) => values.includes(country.code));
    newValue.value.filter.countries = newCountries;
    hasValueChanged.value = true;
  },
});

const selectedLocationTypes = computed({
  get() {
    if (newValue.value?.filter.locationTypes?.length > 0) {
      return newValue.value.filter.locationTypes.map((locationType) => locationType.id);
    }

    return ['all'];
  },
  set(values) {
    const newLocationTypes = metaStore.location_types
      .filter((locationType) => values.includes(locationType.id));
    newValue.value.filter.locationTypes = newLocationTypes;
    hasValueChanged.value = true;
  },
});

const toggleFilters = (event) => {
  if (event?.originalEvent?.dropdownMenuClosed) {
    return;
  }

  showFilters.value = !showFilters.value;

  if (showFilters.value) {
    hasValueChanged.value = false;
    newValue.value = {
      filter: cloneDeep(unref(filter)),
      sort: cloneDeep(unref(sort)),
    };

    if (isLargeScreen.value) {
      $emit('dropdownOpen');
    }
  }
};

const handleClose = () => {
  if (hasValueChanged.value) {
    $emit('update:modelValue', newValue.value);
  }

  hasValueChanged.value = false;
  showFilters.value = false;
  button.value?.$el?.blur();

  if (isLargeScreen.value) {
    $emit('dropdownClose');
  }
};

const handleSortChange = (newSort) => {
  hasValueChanged.value = true;
  newValue.value.sort = newSort;
};

// Display a tooltip 1s after the <CompareOptions> is mounted to inform users that the sort options
// have moved.
// TODO: Remove this code on or after 2025-04-14. The tooltip will be visible for two weeks.

const userStore = useUserStore();
const showTooltip = ref(false);
const tooltipContent = useTemplateRef('tooltipContent');

const hasSeenSortOptionsTooltip = computed({
  get() {
    return localStorageService.getItem('hasSeenSortOptionsTooltip');
  },
  set(value) {
    localStorageService.setItem('hasSeenSortOptionsTooltip', value);
  },
});

const tooltipText = computed(() => {
  let noun;
  let actions;

  if (props.isFavorites && props.isMapPage) {
    noun = 'your favorites map';
  }
  else if (props.isFavorites) {
    noun = 'your favorites list';
  }
  else if (props.isMapPage) {
    noun = 'this map';
  }
  else {
    noun = 'this list';
  }

  if (canShowSort.value && (canShowCountries.value || canShowLocations.value)) {
    actions = 'sorting and filtering';
  }
  else if (canShowSort.value && (!canShowCountries.value && !canShowLocations.value)) {
    actions = 'sorting';
  }
  else {
    actions = 'filtering';
  }

  return `Adjust the ${actions} of ${noun} here.`;
});

const handleCloseTooltip = () => {
  showTooltip.value = false;
  hasSeenSortOptionsTooltip.value = true;
};

const handleShowTooltip = () => {
  if (showTooltip.value) {
    return;
  }

  if (hasSeenSortOptionsTooltip.value) {
    return;
  }

  if (!userStore.isGuest && !hasSeenSortOptionsTooltip.value) {
    showTooltip.value = true;
    onClickOutside(tooltipContent, handleCloseTooltip);
  }
  else {
    showTooltip.value = false;
  }
};

/**
 * When a free user clicks on a disabled button or disabled radio button then navigate to the
 * /user/register or /buy page to either start a free trial or buy All-Access. Note that because
 * the buttons and radio buttons are disabled, slightly round-about document query selector calls
 * are used to check whether one of the disabled elements was clicked.
 */
const handleDisabledClick = (e) => {
  if (isAllAccess.value) {
    return;
  }

  const disabledButton = e?.target?.closest('li')?.querySelector('button[disabled]');
  const disabledRadioButton = e?.target?.closest('li')?.querySelector('input[type="radio"][disabled]');

  if (disabledButton || disabledRadioButton) {
    const { fullPath } = useRoute();
    const router = useRouter();

    const params = {
      isGuest: isGuest.value,
      query: {
        return_to: fullPath,
      },
    };

    const upgradeUrl = getStartFreeTrialUrl(params);
    router.push(upgradeUrl);
  }
};

onMounted(() => window.setTimeout(handleShowTooltip, 1000));
</script>

<template>
  <div
    v-if="canShowCompareOptions"
    :class="[
      containerClass,
      'tw-relative',
      'tw-overflow-visible',
    ]"
  >
    <Button
      ref="button"
      :active="isActive"
      :can-show-chip-active-icon="false"
      :type="ButtonTypes.chipCard"
      @click="toggleFilters"
    >
      <span
        :class="filterIconClass"
        :style="filterIconStyle"
      />
    </Button>
    <Tooltip
      aria-id="has-seen-sort-options-tool-tip"
      container="#root"
      :triggers="[]"
      placement="bottom"
      :shown="showTooltip"
    >
      <template #popper>
        <div
          ref="tooltipContent"
          class="tw-relative"
          @click="handleCloseTooltip"
        >
          <p class="tw-text-left">
            {{ tooltipText }}
          </p>
        </div>
      </template>
    </Tooltip>
    <client-only>
      <component
        :is="component"
        v-bind="componentProps"
        @close="handleClose"
        @close-bottom-sheet="handleClose"
      >
        <template #[slotName]>
          <div :style="slotContentStyle">
            <div
              :class="[
                'tw-overflow-y-auto tw-overscroll-contain',
                'tw-mt-6 lg:tw-mt-0 tw-pb-4 lg:tw-pb-0',
                $style.contentContainer,
              ]"
            >
              <section
                v-if="canShowCountries"
                :class="sectionClass"
                @click="handleDisabledClick"
              >
                <h2 class="tw-pb-4 section-title heading-color">
                  Country
                </h2>
                <Chips
                  v-model="selectedCountries"
                  :can-show-locked-icon="true"
                  :is-all-access="isAllAccess"
                  :options="countries"
                  :select-all-option-value="countries[0].value"
                />
              </section>
              <section
                v-if="canShowLocations"
                class="sectionClass"
                @click="handleDisabledClick"
              >
                <h2 class="tw-pb-4 section-title heading-color">
                  Location Type
                </h2>
                <Chips
                  v-model="selectedLocationTypes"
                  :can-show-locked-icon="true"
                  :is-all-access="isAllAccess"
                  :options="locationTypes"
                  :select-all-option-value="locationTypes[0].value"
                />
              </section>
              <section
                v-if="canShowSort"
                :class="sectionClass"
                @click="handleDisabledClick"
              >
                <h2 class="tw-pb-4 section-title heading-color">
                  Sort By
                </h2>
                <ul :class="sortOptions.length > 4 ? 'tw-columns-2' : null">
                  <li
                    v-for="sortOption in sortOptions"
                    :key="sortOption.field"
                    class="tw-mb-1"
                  >
                    <label
                      :class="isSortOptionLocked(sortOption) ? 'tw-opacity-50' : null"
                    >
                      <RadioButton
                        additional-class-names="tw-align-middle"
                        :checked="isSortOptionChecked(sortOption)"
                        :disabled="isSortOptionLocked(sortOption)"
                        name="sort"
                        :model-value="sortOption.field"
                        @update:model-value="handleSortChange"
                      />
                      <span
                        v-if="isSortOptionLocked(sortOption)"
                        v-bind="lockedIconProps"
                      />
                      <span class="tw-align-middle">
                        {{ sortOption.label }}
                      </span>
                    </label>
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </template>"
      </component>
    </client-only>
  </div>
</template>

<style module>
.contentContainer {
  height: calc(var(--visible-height, 23rem) - 3.25rem);
}

@media (min-width: 992px) {
  .contentContainer {
    height: auto;
  }
}
</style>
